import React, { useEffect } from 'react';
import { Dispatch } from 'react';
import RulaLogo from './components/assets/RulaLogo';
import Login from './login/Login';
import MagicLinkExpired from './login/MagicLinkExpired';
import MagicLinkUsed from './login/MagicLinkUsed';
import SessionExpired from './login/SessionExpired';
import VerifyDob from './login/VerifyDob';
import { Action } from './state/actions';
import { getUTMObject } from './utils/tools';
import { defaultState } from './state/reducer';
import {
  GraduatingMinorEmailTemplates,
  GraduatingMinorEmailTypes,
  UnauthenticatedState,
} from './utils/types';
import VerifyPhoneNumber from './login/VerifyPhoneNumber';
import trackEvent from './utils/amplitude';

export interface UnauthenticatedProps {
  state: UnauthenticatedState;
  dispatch: Dispatch<Action>;
}

const UnauthenticatedContext = React.createContext<{
  state: UnauthenticatedState;
  dispatch: Dispatch<Action>;
}>({
  state: defaultState,
  dispatch: () => {
    console.error('dispatch called before initialization');
  },
});

const getVerificationComponents = (
  state: UnauthenticatedState,
  dispatch: Dispatch<Action>,
) => {
  if (state.ui.status === 'unverified_phone') {
    return (
      <VerifyPhoneNumber
        state={state.ui}
        dispatch={dispatch}
        token={state.token || null}
      />
    );
  }
  if (state.ui.status === 'unverified_birthdate') {
    return (
      <VerifyDob
        state={state.ui}
        dispatch={dispatch}
        token={state.token || null}
      />
    );
  }
  return null;
};

const Unauthenticated = ({ state, dispatch }: UnauthenticatedProps) => {
  const searchParams = new URLSearchParams(window.location.search);
  useEffect(() => {
    const maybeTrackGraduatingMinorEvent = () => {
      // using this instead of FF to determine if we should track. This source is attached to
      // the grad minors new emails as a deeplink param. LD is not reliable at this point
      const pgEmail = searchParams.get('source');

      if (!pgEmail) return;
      const hasTurned18 = searchParams.get('has_turned_18') === 'true';
      const needEmail = searchParams.get('need_minor_email') === 'true';

      const workflow =
        pgEmail === GraduatingMinorEmailTypes.PatientMinorEmail
          ? 'minor'
          : 'guardian';

      const email_template = getEmailTemplate(pgEmail, hasTurned18);
      // in the case of minor workflow, we won't know how they received the finish account setup
      // email. so just defaulting to shared_email
      const graduation_type =
        needEmail || workflow === 'minor' ? 'shared_email' : 'separate_email';

      trackEvent({
        product_area: 'GraduatingMinor',
        name: 'emailCTAClicked',
        trigger: 'Deeplink',
        metadata: { email_template, graduation_type, workflow },
      });
    };

    const getEmailTemplate = (
      pgEmail: string | null,
      hasTurned18: boolean,
    ): string => {
      if (pgEmail === GraduatingMinorEmailTypes.PatientMinorEmail) {
        return GraduatingMinorEmailTemplates.FinishAccountSetup;
      }
      return hasTurned18
        ? GraduatingMinorEmailTemplates.CompleteAccountTransfer
        : GraduatingMinorEmailTemplates.UpcomingAccountTransfer;
    };

    switch (state.ui.status) {
      case 'login':
        trackEvent({
          product_area: 'SignIn',
          name: 'Login_page_viewed',
          trigger: 'Page load',
          ...getUTMObject(searchParams),
        });
        maybeTrackGraduatingMinorEvent();
        break;
      case 'unverified_birthdate':
      case 'unverified_phone':
        trackEvent({
          product_area: 'Verification',
          name: 'Verification_page_viewed',
          trigger: 'Interaction',
        });
        break;
      default:
        break;
    }
  }, [state.ui.status]);
  return (
    <UnauthenticatedContext.Provider value={{ state, dispatch }}>
      <div className="overflow-hidden h-screen relative">
        <div className="flex md:justify-start justify-center mt-5 md:mt-8 md:ml-8">
          <RulaLogo />
        </div>
        <div className="flex w-full h-full flex-col items-center">
          <div className="flex md:min-w-[33%] flex-col px-12 justify-center align-center relative mt-16 md:mt-[160px]">
            {state.ui.status === 'login' && (
              <Login state={state.ui} dispatch={dispatch} />
            )}

            {getVerificationComponents(state, dispatch)}

            {state.ui.status === 'magic_link_expired' && (
              <MagicLinkExpired dispatch={dispatch} />
            )}
            {state.ui.status === 'magic_link_used' && (
              <MagicLinkUsed dispatch={dispatch} />
            )}
            {state.ui.status === 'session_expired' && (
              <SessionExpired dispatch={dispatch} />
            )}
          </div>
        </div>
      </div>
    </UnauthenticatedContext.Provider>
  );
};

export default Unauthenticated;
